import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { LoginForm } from '../components/LoginForm';
import { useAuth } from '../contexts/EnhancedAuthContext';
import { isDevelopment } from '@/utils/environment';
import loginImage from '@/assets/web_image/login_image_2.png';
import logoEmblem from '@/assets/logos/vehix-logo-emblem.png';

type AuthMode = 'login' | 'signup' | 'forgot-password';

interface ForgotPasswordFormProps {
  onBackToLogin: () => void;
}

// eslint-disable-next-line max-lines-per-function
const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onBackToLogin }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setMessage({ type: 'error', text: 'Please enter your email address' });
      return;
    }

    setIsSubmitting(true);
    setMessage(null);

    try {
      // Import authHelpers directly
      const { authHelpers } = await import('@/lib/supabase/client');
      const { error } = await authHelpers.resetPassword(email);

      if (error) {
        setMessage({ type: 'error', text: error.message });
      } else {
        setMessage({
          type: 'success',
          text: 'Password reset link sent! Check your email for instructions.'
        });
        setEmail(''); // Clear email on success
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: 'An unexpected error occurred. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='w-full'>
      <div className='text-center mb-6'>
        <div
          className='w-12 h-12 bg-[#d4854a] rounded-lg flex items-center
            justify-center mx-auto mb-4'
        >
          <span className='text-white font-bold text-xl'>V</span>
        </div>
        <h1 className='text-2xl font-bold text-gray-900'>Reset Password</h1>
        <p className='text-gray-600 mt-2'>Enter your email to receive a password reset link</p>
      </div>

      {message && (
        <div
          className={`mb-4 px-4 py-3 rounded-md text-sm ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
        >
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className='space-y-4'>
        <div>
          <label htmlFor='reset-email' className='block text-sm font-medium text-gray-900 mb-2'>
            Email address
          </label>
          <input
            id='reset-email'
            type='email'
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder='Enter your email'
            disabled={isSubmitting}
            className='w-full px-3 py-2 border border-gray-300 bg-white
              rounded-md focus:outline-none focus:ring-2 focus:ring-[#d4854a]
              focus:border-transparent disabled:opacity-50'
            required
          />
        </div>

        <button
          type='submit'
          disabled={isSubmitting || !email.trim()}
          className='w-full bg-[#d4854a] text-white py-2 px-4 rounded-md
            hover:bg-[#c17640] transition-colors disabled:opacity-50 
            disabled:cursor-not-allowed flex items-center justify-center'
        >
          {isSubmitting ? (
            <>
              <svg
                className='animate-spin -ml-1 mr-3 h-5 w-5 text-white'
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
              >
                <circle
                  className='opacity-25'
                  cx='12'
                  cy='12'
                  r='10'
                  stroke='currentColor'
                  strokeWidth='4'
                />
                <path
                  className='opacity-75'
                  fill='currentColor'
                  // eslint-disable-next-line max-len
                  d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                />
              </svg>
              Sending...
            </>
          ) : (
            'Send Reset Link'
          )}
        </button>

        <div className='text-center'>
          <button
            type='button'
            onClick={onBackToLogin}
            className='text-sm text-[#d4854a] hover:text-[#c17640]'
          >
            Back to Sign In
          </button>
        </div>
      </form>
    </div>
  );
};

interface AuthPageProps {
  /**
   * When 'admin', renders a notice that this login is for Vehix Admins
   * with a link to portal.vehix.ai.
   */
  variant?: 'admin' | 'portal';
}

/* eslint-disable max-lines-per-function */
export const AuthPage: React.FC<AuthPageProps> = ({ variant = 'portal' }) => {
  const [mode, setMode] = useState<AuthMode>('login');
  const { user, loading } = useAuth();
  const isDevMode = isDevelopment();

  const switchToLogin = () => setMode('login');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const switchToSignUp = () => setMode('signup');
  const switchToForgotPassword = () => setMode('forgot-password');

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div
        className='min-h-screen bg-background flex items-center
          justify-center p-4'
      >
        <div className='text-center'>
          <div
            className='animate-spin rounded-full h-8 w-8 border-b-2
              border-brand-primary-600 mx-auto'
          />
          <p className='mt-2 text-brand-neutral-600'>Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect authenticated users to the main app
  if (user) {
    return <Navigate to='/' replace />;
  }

  // Split-screen layout for both dev and production
  return (
    <div className='h-screen w-screen flex flex-col lg:flex-row overflow-hidden relative'>
      {/* Development Environment Indicator - Top Right Corner */}
      {isDevMode && (
        <div className='absolute top-4 right-4 z-10'>
          {/* eslint-disable-next-line max-len */}
          <div className='bg-amber-500 text-black px-3 py-1 rounded-full text-xs font-medium shadow-lg flex items-center space-x-1'>
            <span className='w-2 h-2 bg-red-500 rounded-full animate-pulse' />
            <span>DEV</span>
          </div>
        </div>
      )}

      {/* Left Side - Login Form */}
      <div
        className='flex-1 flex items-center justify-center p-3 sm:p-4 lg:p-8
          bg-[#f3f0e8] relative overflow-y-auto'
      >
        <div className='w-full max-w-md px-2 sm:px-4 md:px-0 py-4'>
          {/* Logo centered above forms - responsive sizing */}
          <div className='flex justify-center mb-4 sm:mb-6 md:mb-8'>
            <img
              src={logoEmblem}
              alt='Vehix Logo'
              // eslint-disable-next-line max-len
              className='w-3/4 sm:w-4/5 md:w-full max-w-[200px] sm:max-w-[250px] md:max-w-full object-contain'
            />
          </div>

          {/* Admin-only notice — only shown on admin.vehix.ai */}
          {variant === 'admin' && mode === 'login' && (
            <div
              className='mb-4 px-4 py-3 rounded-md text-sm bg-amber-50
                border border-amber-200 text-amber-900'
              role='note'
            >
              <div className='font-semibold'>For Vehix Admins only</div>
              <div className='mt-1 text-amber-800'>
                Are you a dealer?{' '}
                <a
                  href='https://portal.vehix.ai'
                  className='underline font-medium hover:text-amber-700'
                >
                  Sign in at portal.vehix.ai
                </a>
              </div>
            </div>
          )}

          {/* Unified Authentication - Same for Dev and Production */}
          {mode === 'login' && <LoginForm onSwitchToForgotPassword={switchToForgotPassword} />}

          {/* Signup is disabled - users are created by admin */}
          {mode === 'signup' && (
            <div className='w-full max-w-md'>
              <div className='mb-6'>
                <h1 className='text-2xl sm:text-3xl font-bold text-gray-900 mb-2'>
                  Account Creation
                </h1>
                <p className='text-sm sm:text-base text-gray-600'>
                  User accounts are created by administrators. Please contact your administrator to
                  request an account.
                </p>
              </div>
              <div className='mt-4 text-center'>
                <button
                  onClick={switchToLogin}
                  className='text-[#d4854a] hover:text-[#c17640] font-semibold'
                >
                  Back to Sign In
                </button>
              </div>
            </div>
          )}

          {mode === 'forgot-password' && <ForgotPasswordForm onBackToLogin={switchToLogin} />}
        </div>
      </div>

      {/* Right Side - Brand Image */}
      <div className='hidden lg:block lg:flex-1 relative overflow-hidden'>
        <img src={loginImage} alt='Vehix' className='absolute inset-0 w-full h-full object-cover' />
      </div>
    </div>
  );
};
